import React, { useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import { ThemeProvider } from "./context/ThemeContext";
import { languageCodes, defaultLanguage } from "./i18n";
import LanguageRouter from "./components/LanguageRouter";
import { trackPageView } from "./lib/analytics";

// Scroll to top on route change + track page views
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    // Track page view for analytics
    trackPageView(pathname);
  }, [pathname]);

  return null;
}

// Eagerly loaded (always needed)
import AstraLandingPage from "./pages/AstraLandingPage";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";

// Tenant provider + broker-branded landing (multi-tenant public site)
import { TenantProvider, useTenant } from "./tenant/TenantContext";
const BrokerLandingPage = lazy(() => import("./tenant/BrokerLandingPage"));
const TemplatePreviewPage = lazy(() => import("./tenant/TemplatePreviewPage"));
const LandingEditor = lazy(() => import("./platform/admin/LandingEditor"));

// Founder profile — SEO-optimized (JSON-LD Person, OG profile, canonical)
const AmiranAzaladzePage = lazy(() => import("./pages/AmiranAzaladzePage"));

// Lazy-loaded pages (only loaded when navigated to)
const AuthPage = lazy(() => import("./pages/AuthPage"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage"));
const PaymentCancelPage = lazy(() => import("./pages/PaymentCancelPage"));
const SetupBrokerPage = lazy(() => import("./pages/SetupBrokerPage"));
const AdminLoginPage = lazy(() => import("./pages/AdminLoginPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));

// Lazy wrappers for named exports from LegalPages
const PrivacyPolicyPage = lazy(() => import("./pages/LegalPages").then(m => ({ default: m.PrivacyPolicyPage })));
const TermsOfServicePage = lazy(() => import("./pages/LegalPages").then(m => ({ default: m.TermsOfServicePage })));
const RiskDisclosurePage = lazy(() => import("./pages/LegalPages").then(m => ({ default: m.RiskDisclosurePage })));
const CookiePolicyPage = lazy(() => import("./pages/LegalPages").then(m => ({ default: m.CookiePolicyPage })));
const SecurityPage = lazy(() => import("./pages/LegalPages").then(m => ({ default: m.SecurityPage })));
const AstraTradingTerminal = lazy(() => import("./pages/AstraTradingTerminal"));
const AstraPlatformPage = lazy(() => import("./pages/AstraPlatformPage"));
const StandaloneTradingPage = lazy(() => import("./pages/StandaloneTradingPage"));
const ChartWidget = lazy(() => import("./pages/ChartWidget"));
const LandingPageViewer = lazy(() => import("./pages/LandingPageViewer"));

// Widgets & API (embed service)
const WidgetsLandingPage = lazy(() => import("./pages/widgets/WidgetsLandingPage"));
const WidgetsLoginPage = lazy(() => import("./pages/widgets/WidgetsLoginPage"));
const WidgetsRegisterPage = lazy(() => import("./pages/widgets/WidgetsRegisterPage"));
const WidgetsDashboardLayout = lazy(() => import("./pages/widgets/WidgetsDashboardLayout"));
const WidgetsPage = lazy(() => import("./pages/widgets/WidgetsPage"));
const ApiDocsPage = lazy(() => import("./pages/widgets/ApiDocsPage"));
const BillingPage = lazy(() => import("./pages/widgets/BillingPage"));
const DedicatedServerPage = lazy(() => import("./pages/widgets/DedicatedServerPage"));
const WidgetsSettingsPage = lazy(() => import("./pages/widgets/SettingsPage"));
const EmbedChartPage = lazy(() => import("./pages/widgets/EmbedChartPage"));
const WidgetsAuthCallback = lazy(() => import("./pages/widgets/AuthCallbackPage"));
const PreviewChartPage = lazy(() => import("./pages/widgets/PreviewChartPage"));
const ApiDocumentationPage = lazy(() => import("./pages/ApiDocumentationPage"));
const EmbedDocsPage = lazy(() => import("./pages/EmbedDocsPage"));

// ============= ASTRA TRADING PLATFORM (White-Label) =============
// These routes belong to the separate trading platform product.
// When deployed independently, these move to their own App.js.
const BrokerAdminLayout = lazy(() => import("./platform/admin/AdminLayout"));
const BrokerAdminLogin = lazy(() => import("./platform/admin/AdminLogin"));
const BrokerDashboard = lazy(() => import("./platform/admin/Dashboard"));
const BrokerClients = lazy(() => import("./platform/admin/ClientsPage"));
const BrokerTrading = lazy(() => import("./platform/admin/TradingMonitor"));
const BrokerDeposits = lazy(() => import("./platform/admin/DepositsPage"));
const BrokerCryptoPayments = lazy(() => import("./platform/admin/CryptoPaymentsPage"));
const BrokerKycReview = lazy(() => import("./platform/admin/KycReviewPage"));
const BrokerAmlReview = lazy(() => import("./platform/admin/AmlCasesPage"));
const BrokerGroups = lazy(() => import("./platform/admin/GroupsPage"));
const BrokerLandingPages = lazy(() => import("./platform/admin/LandingPagesPage"));
const BrokerPageBuilder = lazy(() => import("./platform/admin/PageBuilder"));
const BrokerDomains = lazy(() => import("./platform/admin/DomainsPage"));
const BrokerTeam = lazy(() => import("./platform/admin/TeamPage"));
const BrokerIB = lazy(() => import("./platform/admin/IBPage"));
const BrokerMessaging = lazy(() => import("./platform/admin/MessagingPage"));
const BrokerSocialTrading = lazy(() => import("./platform/admin/SocialTradingPage"));
const BrokerExecutionModel = lazy(() => import("./platform/admin/ExecutionModelPage"));
const BrokerRiskManagement = lazy(() => import("./platform/admin/RiskManagementPage"));
const BrokerInstruments = lazy(() => import("./platform/admin/InstrumentsPage"));
const SuperAdminDocsPage = lazy(() => import("./pages/SuperAdminDocsPage"));
const MarketProviderPage = lazy(() => import("./pages/MarketProviderPage"));
const TerminalArchitecturePage = lazy(() => import("./pages/TerminalArchitecturePage"));
const TerminalThemesPage = lazy(() => import("./pages/TerminalThemesPage"));
const LiveChartPage = lazy(() => import("./pages/DemoPage"));
const FeaturesPage = lazy(() => import("./pages/FeaturesPage"));
const DemoMaterialsPage = lazy(() => import("./pages/DemoMaterialsPage"));
const BrokerOnboardingGuidePage = lazy(() => import("./pages/BrokerOnboardingGuidePage"));
const AppGuidePage = lazy(() => import("./pages/AppGuidePage"));
const OnboardClientPage = lazy(() => import("./pages/OnboardClientPage"));
const ClientLayout = lazy(() => import("./platform/client/ClientLayout"));
const TraderLoginPage = lazy(() => import("./pages/TraderLoginPage"));
const ClientLogin = lazy(() => import("./platform/client/ClientAuth").then(m => ({ default: m.ClientLogin })));
const ClientRegister = lazy(() => import("./platform/client/ClientAuth").then(m => ({ default: m.ClientRegister })));
const ClientDashboard = lazy(() => import("./platform/client/ClientDashboard"));
const ClientAccounts = lazy(() => import("./platform/client/AccountsPage"));
const ClientDeposit = lazy(() => import("./platform/client/DepositPage"));
const ClientKyc = lazy(() => import("./platform/client/KycPage"));
const ClientKycReturn = lazy(() => import("./platform/client/KycReturnPage"));
const ClientDownloads = lazy(() => import("./platform/client/DownloadsPage"));
const ClientInbox = lazy(() => import("./platform/client/InboxPage"));
const ClientSocialTrading = lazy(() => import("./platform/client/SocialTradingPage"));
const ClientInvestments = lazy(() => import("./platform/client/InvestmentsPage"));

// Cosmic on-brand page loader
import CosmicLoader from "./components/CosmicLoader";
function PageLoader() {
  return <CosmicLoader fullscreen />;
}

// Pages that should NOT have navbar/footer (standalone layouts)
const standalonePages = ['/dashboard', '/admin', '/platform', '/astratradingterminal', '/trading', '/widget', '/embed', '/preview', '/widgets-api', '/widgets', '/lp', '/', '/live-chart', '/features', '/legal', '/login', '/register', '/auth-callback', '/docs', '/b', '/amiran-azaladze', '/Amiran-azaladze', '/Amiran-Azaladze'];

// All application routes - defined once and reused
// Root component: swaps in the broker's branded landing when the URL indicates a tenant.
// If a subdomain/path context is present, ALWAYS render BrokerLandingPage — it handles
// both success and "broker not found" states cleanly. We only fall back to the marketing
// site when no tenant context was detected at all (i.e. plain astraterminal.net).
function RootPage() {
  const { detected, loading } = useTenant();
  if (loading && detected) return null;   // cosmic loader is already visible
  if (detected) return <BrokerLandingPage />;
  return <AstraLandingPage />;
}

const appRoutes = [
  { path: "/", element: <RootPage /> },
  // Path-based tenant preview: /b/<slug>  — the tenant slug is remembered in sessionStorage
  // so subsequent in-SPA navigation (e.g. to /platform/client/login) stays tenant-scoped.
  { path: "/b/:slug", element: <BrokerLandingPage /> },
  // Standalone template previews — used by the hover-thumbnail picker in
  // Broker Admin (Branded Homepages editor) and Super Admin (onboarding step 5).
  { path: "/preview/landing/:templateId", element: <TemplatePreviewPage /> },
  { path: "/portal", element: <Navigate to="/" replace /> },
  { path: "/admin/docs", element: <SuperAdminDocsPage /> },
  { path: "/admin/market-provider", element: <MarketProviderPage /> },
  { path: "/admin/terminal-architecture", element: <TerminalArchitecturePage /> },
  { path: "/admin/terminal-themes", element: <TerminalThemesPage /> },
  { path: "/admin/demo-materials", element: <DemoMaterialsPage /> },
  { path: "/admin/onboarding-guide", element: <BrokerOnboardingGuidePage /> },
  { path: "/admin/app/:platform", element: <AppGuidePage /> },
  { path: "/admin/onboard-client", element: <OnboardClientPage /> },
  { path: "/demo", element: <LiveChartPage /> },
  { path: "/live-chart", element: <LiveChartPage /> },
  { path: "/features", element: <FeaturesPage /> },
  { path: "/about", element: <Navigate to="/" replace /> },
  { path: "/brokers", element: <Navigate to="/" replace /> },
  { path: "/brokers/:id", element: <Navigate to="/" replace /> },
  { path: "/charts", element: <Navigate to="/" replace /> },
  { path: "/calendar", element: <Navigate to="/" replace /> },
  { path: "/news", element: <Navigate to="/" replace /> },
  { path: "/news/article/:articleId", element: <Navigate to="/" replace /> },
  { path: "/toolbox", element: <Navigate to="/" replace /> },
  { path: "/traders", element: <Navigate to="/" replace /> },
  { path: "/traders/:traderId", element: <Navigate to="/" replace /> },
  { path: "/auth", element: <AuthPage /> },
  { path: "/auth/callback", element: <AuthCallback /> },
  { path: "/dashboard", element: <DashboardPage /> },
  { path: "/login", element: <TraderLoginPage /> },
  { path: "/register", element: <TraderLoginPage /> },
  /* Founder profile — SEO-optimized (JSON-LD Person, OG profile, canonical) */
  { path: "/amiran-azaladze", element: <AmiranAzaladzePage /> },
  { path: "/Amiran-azaladze", element: <Navigate to="/amiran-azaladze" replace /> },
  { path: "/Amiran-Azaladze", element: <Navigate to="/amiran-azaladze" replace /> },
  { path: "/auth-callback", element: <WidgetsAuthCallback /> },
  /* B2B widgets partner login lives at its own path (used to share /login) */
  { path: "/widgets/login", element: <WidgetsLoginPage /> },
  { path: "/widgets/register", element: <WidgetsRegisterPage /> },
  { path: "/widgets/auth-callback", element: <WidgetsAuthCallback /> },
  { path: "/payment/success", element: <PaymentSuccessPage /> },
  { path: "/payment/cancel", element: <PaymentCancelPage /> },
  { path: "/awards", element: <Navigate to="/" replace /> },
  { path: "/awards/nominee/:id", element: <Navigate to="/" replace /> },
  { path: "/tournament", element: <Navigate to="/" replace /> },
  { path: "/setup-broker", element: <SetupBrokerPage /> },
  { path: "/admin", element: <AdminPage /> },
  { path: "/admin/login", element: <AdminLoginPage /> },
  { path: "/contact", element: <Navigate to="/" replace /> },
  { path: "/careers", element: <Navigate to="/" replace /> },
  { path: "/press", element: <Navigate to="/" replace /> },
  { path: "/legal/privacy", element: <PrivacyPolicyPage /> },
  { path: "/legal/terms", element: <TermsOfServicePage /> },
  { path: "/legal/risk", element: <RiskDisclosurePage /> },
  { path: "/legal/cookies", element: <CookiePolicyPage /> },
  { path: "/legal/security", element: <SecurityPage /> },
  { path: "/analysis", element: <Navigate to="/" replace /> },
  { path: "/analysis/:postId", element: <Navigate to="/" replace /> },
  { path: "/education", element: <Navigate to="/" replace /> },
  { path: "/education/course/:courseId", element: <Navigate to="/" replace /> },
  { path: "/education/lesson/:lessonId", element: <Navigate to="/" replace /> },
  { path: "/astratradingterminal", element: <AstraTradingTerminal /> },
  { path: "/astra-platform", element: <AstraPlatformPage /> },
  { path: "/trading", element: <StandaloneTradingPage /> },
  { path: "/widget/chart", element: <ChartWidget /> },
  { path: "/embed/chart", element: <EmbedChartPage /> },
  { path: "/preview/chart", element: <PreviewChartPage /> },
  { path: "/widgets-api", element: <WidgetsLandingPage /> },
  { path: "/docs/api", element: <ApiDocumentationPage /> },
  { path: "/docs/embed", element: <EmbedDocsPage /> },
  { path: "/widgets/dashboard", element: <WidgetsDashboardLayout />,
    children: [
      { index: true, element: <WidgetsPage /> },
      { path: "widgets", element: <WidgetsPage /> },
      { path: "api", element: <ApiDocsPage /> },
      { path: "billing", element: <BillingPage /> },
      { path: "dedicated", element: <DedicatedServerPage /> },
      { path: "settings", element: <WidgetsSettingsPage /> },
    ],
  },
  { path: "/lp/:slug", element: <LandingPageViewer /> },
];

// Paths reachable on a tenant host (broker subdomain OR broker custom domain).
// Anything NOT in this list is a platform-only page and must NEVER render on a tenant host —
// otherwise a broker's clients would see AstraTerminal marketing, the Super Admin login,
// the B2B partner portal, etc. on their broker's own domain.
const TENANT_ALLOWED_EXACT = new Set(['/', '/trading', '/astratradingterminal']);
const TENANT_ALLOWED_PREFIXES = ['/b/', '/platform/', '/lp/', '/legal/', '/preview/', '/embed/', '/widget/'];
function isAllowedOnTenant(path) {
  if (TENANT_ALLOWED_EXACT.has(path)) return true;
  return TENANT_ALLOWED_PREFIXES.some(p => path.startsWith(p));
}

function AppLayout() {
  const location = useLocation();
  const { detected: tenantDetected, loading: tenantLoading } = useTenant();
  
  // Check if current path is standalone (with or without language prefix)
  const pathParts = location.pathname.split('/').filter(Boolean);
  const firstPart = pathParts[0];
  const isLangPrefix = languageCodes.includes(firstPart);
  const checkPath = isLangPrefix ? `/${pathParts.slice(1).join('/')}` : location.pathname;
  const isStandalonePage = checkPath === '/' || standalonePages.some(path => path !== '/' && checkPath.startsWith(path));

  // Tenant route guard: on a broker's own host (subdomain or custom domain),
  // block every platform-exclusive route and send the visitor back to the broker's landing.
  // We wait for tenant resolution to finish so we don't flash the platform site or redirect
  // legitimate platform visitors during the initial detect.
  if (tenantDetected && !tenantLoading && !isAllowedOnTenant(checkPath)) {
    return <Navigate to="/" replace data-testid="tenant-route-block" />;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <ScrollToTop />
      {!isStandalonePage && <Navbar />}
      
      <main className={isStandalonePage ? "" : "flex-1"}>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Default routes (English - no prefix) */}
            <Route element={<LanguageRouter />}>
              {appRoutes.map(({ path, element, children }) => (
                <Route key={path} path={path} element={element}>
                  {children?.map((c, i) => (
                    <Route
                      key={c.path || `index-${i}`}
                      {...(c.index ? { index: true } : { path: c.path })}
                      element={c.element}
                    />
                  ))}
                </Route>
              ))}
            </Route>
            
            {/* Language-prefixed routes */}
            <Route path="/:lang" element={<LanguageRouter />}>
              {appRoutes.map(({ path, element, children }) => (
                <Route 
                  key={`lang-${path}`} 
                  path={path === "/" ? "" : path.slice(1)} 
                  element={element} 
                >
                  {children?.map((c, i) => (
                    <Route
                      key={`lang-${c.path || `index-${i}`}`}
                      {...(c.index ? { index: true } : { path: c.path })}
                      element={c.element}
                    />
                  ))}
                </Route>
              ))}
            </Route>

            {/* Platform - Broker Admin */}
            <Route path="/platform/admin/login" element={<BrokerAdminLogin />} />
            <Route path="/platform/admin" element={<BrokerAdminLayout />}>
              <Route index element={<BrokerDashboard />} />
              <Route path="clients" element={<BrokerClients />} />
              <Route path="trading" element={<BrokerTrading />} />
              <Route path="deposits" element={<BrokerDeposits />} />
              <Route path="crypto" element={<BrokerCryptoPayments />} />
              <Route path="kyc" element={<BrokerKycReview />} />
              <Route path="aml" element={<BrokerAmlReview />} />
              <Route path="groups" element={<BrokerGroups />} />
              <Route path="pages" element={<BrokerLandingPages />} />
              <Route path="pages/landing/:brokerId/edit" element={<LandingEditor />} />
              <Route path="pages/:pageId" element={<BrokerPageBuilder />} />
              <Route path="domains" element={<BrokerDomains />} />
              <Route path="team" element={<BrokerTeam />} />
              <Route path="ib" element={<BrokerIB />} />
              <Route path="messaging" element={<BrokerMessaging />} />
              <Route path="social-trading" element={<BrokerSocialTrading />} />
              <Route path="execution" element={<BrokerExecutionModel />} />
              <Route path="risk" element={<BrokerRiskManagement />} />
              <Route path="instruments" element={<BrokerInstruments />} />
            </Route>

            {/* Platform - Client Portal */}
            <Route path="/platform/client/login" element={<ClientLogin />} />
            <Route path="/platform/client/register" element={<ClientRegister />} />
            <Route path="/platform/client" element={<ClientLayout />}>
              <Route index element={<ClientDashboard />} />
              <Route path="accounts" element={<ClientAccounts />} />
              <Route path="deposit" element={<ClientDeposit />} />
              <Route path="kyc" element={<ClientKyc />} />
              <Route path="kyc/return" element={<ClientKycReturn />} />
              <Route path="downloads" element={<ClientDownloads />} />
              <Route path="inbox" element={<ClientInbox />} />
              <Route path="social-trading" element={<ClientSocialTrading />} />
              <Route path="investments" element={<ClientInvestments />} />
            </Route>
            
            {/* Fallback - redirect unknown routes to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>
      
      {!isStandalonePage && <Footer />}
      
      <Toaster 
        position="bottom-right" 
        richColors 
        closeButton 
      />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <BrowserRouter>
          <TenantProvider>
            <AppLayout />
          </TenantProvider>
        </BrowserRouter>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
